const MAX_DIMENSION = 1920;
const JPEG_QUALITY = 0.82;
const SKIP_THRESHOLD_BYTES = 200 * 1024; // 200KB

/**
 * Shrinks image before upload using browser canvas.
 * - Max 1920px on longest side, keeps aspect ratio
 * - Outputs JPEG at 82% quality
 * - Skips files under 200KB
 * - Returns original if result is bigger
 * - On any error returns original file unchanged
 */
export const compressImage = (file: File): Promise<File> => {
  if (!file.type.startsWith('image/')) {
    return Promise.resolve(file);
  }

  if (file.size <= SKIP_THRESHOLD_BYTES) {
    return Promise.resolve(file);
  }

  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      const { width, height } = img;

      // No resize needed if already within bounds
      if (width <= MAX_DIMENSION && height <= MAX_DIMENSION && file.type === 'image/jpeg') {
        resolve(file);
        return;
      }

      // Calculate new dimensions preserving aspect ratio
      let newWidth = width;
      let newHeight = height;

      if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
        if (width > height) {
          newWidth = MAX_DIMENSION;
          newHeight = Math.round((height / width) * MAX_DIMENSION);
        } else {
          newHeight = MAX_DIMENSION;
          newWidth = Math.round((width / height) * MAX_DIMENSION);
        }
      }

      const canvas = document.createElement('canvas');
      canvas.width = newWidth;
      canvas.height = newHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }

      ctx.drawImage(img, 0, 0, newWidth, newHeight);

      canvas.toBlob(
        (blob) => {
          if (!blob || blob.size >= file.size) {
            resolve(file);
            return;
          }

          const compressedFile = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });

          resolve(compressedFile);
        },
        'image/jpeg',
        JPEG_QUALITY,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };

    img.src = url;
  });
};

export const compressFormDataImage = async (formData: FormData): Promise<FormData> => {
  const file = formData.get('file');

  if (!(file instanceof File)) {
    return formData;
  }

  const compressed = await compressImage(file);

  if (compressed === file) {
    return formData;
  }

  const newFormData = new FormData();
  newFormData.append('file', compressed);

  // Preserve any other fields from the original FormData
  formData.forEach((value, key) => {
    if (key !== 'file') {
      newFormData.append(key, value);
    }
  });

  return newFormData;
};
